/* Roteamento do eyecarebi-frontend
 *
 * Lazy loading: todas as views são carregadas sob demanda.
 * Apenas os módulos essenciais (Home layout, NoAccess, Maintenance) são eager.
 */

import Vue from 'vue'
import VueRouter from 'vue-router'

import Api from '../utils/api'
import { userHasPermission } from '@/utils/localStorageManager'
import {
  showSurgeryCenterV2,
  showCompras,
  showBillingReact,
  showDoctorOverview
} from '@/utils/permissionsHelp'
import { MAINTENANCE_MODE } from '../config/maintenance'
import { datadogRum } from '@datadog/browser-rum'

import { handleRouterError } from '@/utils/routerErrorHandler'

// Eager: layout principal + rotas de fallback (sempre necessárias)
import Home from '../views/Home.vue'
import NoAccess from '../views/NoAccess.vue'
import Maintenance from '../views/Maintenance.vue'

// Lazy: todas as views carregadas sob demanda
const Login = () => import('@/modules/login/views/Login')
const Register = () => import('../views/Register.vue')
const ProductionSheet = () => import('../views/ProductionSheet.vue')
const SecretaryRegister = () => import('../views/SecretaryRegister.vue')
const ManagerRegister = () => import('../views/ManagerRegister.vue')
const NurseRegister = () => import('../views/NurseRegister.vue')
const TechRegister = () => import('../views/TechRegister.vue')
const DoctorRegister = () => import('../views/DoctorRegister')
const Professional = () => import('../views/Professional.vue')
const Secretary = () => import('../views/Secretary.vue')
const ForgotPassword = () => import('../views/ForgotPassword.vue')
const ResetPassword = () => import('../views/ResetPassword.vue')
const RequestDemo = () => import('../views/RequestDemo.vue')
const ConsentForm = () => import('../views/ConsentForm.vue')
const PublicScheduleView = () => import('../views/Configs/PublicScheduleView')
const OverviewView = () => import('../views/Overview/OverviewReact.vue')
const PreConsultation = () => import('../views/PreConsultation')
const PreConsultationForm = () => import('../views/PreConsultation/Form')
const ChatView = () => import('../views/Chat/ChatView')
const PatientForm = () => import('../views/Patients/PatientForm')
const NewAttendance = () => import('../views/Patients/NewAttendance')
const NewAnnotation = () => import('../views/Patients/NewAnnotation')
const PrintAttendance = () => import('../views/Patients/PrintAttendance')
const AnalyticsView = () => import('../views/Analytics/AnalyticsView')
const PersonalDataView = () => import('../views/Configs/PersonalDataReact.vue')
const ClinicSettingsView = () => import('../views/Configs/ClinicSettingsView')
const Warehouse = () => import('../views/Warehouse/Warehouse')
// Cutover fase 2: callback de assinatura em React (PrepareFiles.vue sai na fase 3.1)
const PrepareFiles = () => import('../views/PrepareFiles/PrepareFilesReact')
const Protocols = () => import('../views/Protocols/Protocols')
const CallPanel = () => import('../views/CallPanel')
const Referrals = () => import('../views/Referrals')
const CallPanelTelevisor = () => import('../views/CallPanel/Televisor')
// Registros padrões: as 3 telas viraram uma ilha React só (a rota diz qual).
const StandardRecordsReact = () =>
  import('../views/StandardRecords/StandardRecordsReact.vue')
const ManagementReports = () => import('../views/ManagementReports')
const MedicalReports = () => import('../views/MedicalReports')
const MedicalReport = () => import('../views/MedicalReports/MedicalReport.vue')
const Academy = () => import('../views/Academy')
const CourseView = () => import('../views/Academy/CourseView')
const RootManager = () => import('../views/RootManager')
const Attendance = () => import('../layouts/Attendance')
const CourseDashboard = () => import('../layouts/Academy/CourseDashboard')
const SignLogs = () => import('../layouts/SignLogs')
const PatientPrescription = () =>
  import('../views/Prescriptions/PatientPrescription.vue')
const DocByCode = () => import('../views/Prescriptions/DocByCode.vue')
const PharmacyPrescription = () =>
  import('../views/Prescriptions/PharmacyPrescription.vue')
const OculosPrescription = () =>
  import('../views/Prescriptions/OculosPrescription.vue')
const SellingPanelHome = () =>
  import('@/views/SellingPanel/SellingPanelHome.vue')
const SurgeryCenterReact = () =>
  import('@/views/SurgeryCenter/SurgeryCenterReact.vue')
// Novo módulo Compras, do zero em React (política: toda feature nova nasce em React).
const ComprasReact = () => import('@/views/Compras/ComprasReact.vue')
// Portal PÚBLICO do fornecedor (Compras — Fase 3). Sem login: autentica pelo
// token opaco da URL (/cotacao/:token).
const CotacaoPortalReact = () =>
  import('@/views/Compras/CotacaoPortalReact.vue')
// Página PÚBLICA de captura pelo celular pareado (sem login do app).
const MobileCaptureReact = () =>
  import('@/views/MobileCapture/MobileCaptureReact.vue')
// Spike ADR-016: guia de Consulta TISS no motor de formulário declarativo
// (react-apps/billing). Rota standalone atrás de flag — não toca o fluxo Vue.
const BillingSpikeReact = () => import('@/views/Billing/BillingSpikeReact.vue')
// Página única de guia (ADR-016 F4): serve os 8 tipos do registry pelo :tipo.
const BillingGuideReact = () => import('@/views/Billing/BillingGuideReact.vue')
// Workspace de guias do faturamento React (ADR-016 F2): fila transversal
// sobre v2/tiss/workspace/* — mesma flag `billing-react` do spike.
const BillingWorkspaceReact = () =>
  import('@/views/Billing/BillingWorkspaceReact.vue')
// Gerenciar guias do lote em React (ADR-016 F2) — rota própria dentro do
// workspace, mesma flag `billing-react`.
const BillingBatchGuidesReact = () =>
  import('@/views/Billing/BillingBatchGuidesReact.vue')

/***
 * Classe para roteamento do VUE framework, para os componentes das views
 */
Vue.use(VueRouter)

// Constante router com as lista de rotas.
const routes = [
  { path: '/manutencao', component: Maintenance, name: 'maintenance' },
  { path: '/acesso-negado', component: NoAccess, name: 'noAccess' },
  { path: '/login/:confirmationCode?', component: Login },
  {
    // Captura via Celular Pareado (Fase 0). PÚBLICA: o celular abre via QR,
    // SEM login do app — autentica pelo pairing token na URL (/m/captura?t=...).
    // Fica FORA do layout Home (full screen, sem sidebar/header). O guard
    // beforeEach só bloqueia rotas com meta.requiresAuth; como esta NÃO tem,
    // já passa livre — meta.public é apenas declarativo (mesmo padrão de /login).
    path: '/m/captura',
    name: 'mobileCapture',
    component: MobileCaptureReact,
    meta: { public: true }
  },
  {
    // Portal público do fornecedor (Compras — Fase 3). Sem login: o guard
    // beforeEach só bloqueia meta.requiresAuth, então esta passa livre.
    path: '/cotacao/:token',
    name: 'cotacaoPortal',
    component: CotacaoPortalReact,
    meta: { public: true }
  },
  {
    path: '/agendamento/paciente/:appointmentId',
    component: () => import('@/views/AppointmentPublicConfirmation')
  },
  {
    path: '/convite/:invitationCode/secretaria',
    component: SecretaryRegister
  },
  {
    path: '/convite/:invitationCode/GESTOR',
    component: ManagerRegister
  },
  { path: '/convite/:invitationCode/enfermeiro', component: NurseRegister },
  { path: '/convite/:invitationCode/tecnico', component: TechRegister },
  { path: '/convite/:invitationCode/medico', component: DoctorRegister },
  { path: '/convite/:invitationCode/anestesista', component: DoctorRegister },
  { path: '/profissionais/:invitationCode', component: Professional },
  { path: '/secretarias/:invitationCode', component: Secretary },
  { path: '/esqueci-minha-senha', component: ForgotPassword },
  { path: '/esqueci-minha-senha/:token', component: ResetPassword },
  { path: '/solicitar-demonstracao', component: RequestDemo },
  { path: '/cadastre-se', redirect: '/solicitar-demonstracao' },
  { path: '/cadastre-se/:registerKey', component: Register }, // redirect: '/login' },
  { path: '/folha-de-producao', component: ProductionSheet },
  { path: '/termo-de-consentimento', component: ConsentForm },
  { path: '/receita-paciente/:attendanceid', component: PatientPrescription },
  { path: '/doc/:code', component: DocByCode },
  { path: '/receita-farmacia/:attendanceid', component: PharmacyPrescription },
  { path: '/receita-oculos/:attendanceid', component: OculosPrescription },
  {
    path: '/pacientes/:id/consulta/:idConsulta/imprimir',
    component: PrintAttendance,
    meta: { requiresAuth: true }
  },
  {
    path: '/preparando-documentos',
    component: PrepareFiles,
    meta: { requiresAuth: true }
  },
  {
    path: '/admin',
    component: RootManager,
    meta: { requiresAuth: true }
  },
  {
    path: '/admin/academy/course-dashboard/:courseId',
    component: CourseDashboard,
    meta: { requiresAuth: true }
  },
  {
    path: '/chamadas',
    name: 'CallPanelTelevisor',
    component: CallPanelTelevisor,
    props: true,
    meta: { requiresAuth: true }
  },
  {
    path: '/',
    component: Home,
    children: [
      {
        path: '',
        redirect: () => {
          const clinic = JSON.parse(localStorage.getItem('clinic'))
          return clinic
            ? clinic.role === 'DOCTOR' ||
              clinic.role === 'DOCTOR_MANAGER' ||
              !userHasPermission('FpAgen1')
              ? '/visao-geral'
              : '/agenda'
            : '/login'
        }
      },
      {
        path: 'pre-consulta',
        component: PreConsultation,
        meta: { requiresAuth: true },
        beforeEnter: (to, from, next) =>
          customBeforeEnter('FpPreCon1', to, from, next)
      },
      {
        path: 'pre-consulta/:id',
        component: PreConsultationForm,
        meta: { requiresAuth: true },
        beforeEnter: (to, from, next) =>
          customBeforeEnter('FpPreCon2', to, from, next)
      },
      {
        path: 'exames',
        component: () => import('@/views/Exam/Exams'),
        meta: { requiresAuth: true },
        beforeEnter: (to, from, next) =>
          customBeforeEnter('FpExam1', to, from, next)
      },
      {
        path: 'pacientes/:patientId/exame/:attendanceId',
        component: Attendance,
        meta: { requiresAuth: true },
        beforeEnter: (to, from, next) =>
          customBeforeEnter('FpExam2', to, from, next)
      },
      {
        path: 'estoque/:tab?',
        component: Warehouse,
        meta: { requiresAuth: true },
        beforeEnter: (to, from, next) =>
          customBeforeEnter('Estoque', to, from, next)
      },
      {
        // Módulo Compras, novo do zero. Gate só por feature flag por clínica
        // (clinic_features, key `compras`) — sem permissão de perfil ainda.
        path: 'compras/:pathMatch(.*)*',
        name: 'ComprasReact',
        component: ComprasReact,
        meta: { requiresAuth: true },
        beforeEnter: (to, from, next) => (showCompras() ? next() : next('/'))
      },
      // ── Faturamento em React (ADR-016) ─────────────────────────────────
      // Endereço NEUTRO de regime (card 86akah1ud): a tela serve TISS e SUS e
      // o regime é seletor dentro dela (f_regime/l_regime/c_regime na URL).
      // Herdava `faturamento-tiss/workspace/*`, por cima do namespace do
      // módulo Vue TISS legado — uma guia APAC respondia num endereço que
      // anunciava TISS. Os caminhos vivem em `react-apps/billing/routes.ts`.
      // Gate por feature flag por clínica (clinic_features, `billing-react`).
      {
        // Spike do motor de formulário (guia de Consulta). Edição: ?guia=<id>.
        path: 'faturamento/spike/guia-consulta',
        name: 'BillingSpikeConsulta',
        component: BillingSpikeReact,
        meta: { requiresAuth: true },
        beforeEnter: (to, from, next) =>
          showBillingReact() ? next() : next('/')
      },
      {
        // Página ÚNICA de guia (F4): consultation | spsadt | fee |
        // hospitalization-request | hospitalization-summary | other-expenses |
        // apac | bpa | aih — ou `nova` (o tipo sai da configuração do
        // convênio). Edição via ?guia=<tiss_guide_id>.
        path: 'faturamento/guia/:tipo',
        name: 'BillingGuide',
        component: BillingGuideReact,
        meta: { requiresAuth: true },
        beforeEnter: (to, from, next) =>
          showBillingReact() ? next() : next('/')
      },
      {
        // Gerenciar guias do lote (F2): tela de BAIXA do retorno da
        // operadora. Declarada ANTES do workspace porque o `:tab` da rota
        // abaixo é opcional e casaria com 'lotes' antes de chegar aqui.
        path: 'faturamento/lotes/:batchId/gerenciar-guias',
        name: 'BillingManageBatchGuides',
        component: BillingBatchGuidesReact,
        meta: { requiresAuth: true },
        beforeEnter: (to, from, next) =>
          showBillingReact() ? next() : next('/')
      },
      {
        // Workspace (F2): fila transversal do escopo do faturista
        // (v2/tiss/workspace/*). Rota nua = dashboard.
        path: 'faturamento/:tab(guias|lotes|dashboard|faturas|configuracoes)?',
        name: 'BillingWorkspace',
        component: BillingWorkspaceReact,
        meta: { requiresAuth: true },
        beforeEnter: (to, from, next) =>
          showBillingReact() ? next() : next('/')
      },
      // Endereços ANTIGOS das telas React → novos, com a query intacta
      // (?guia=, ?pendencias=, filtros f_*/l_*/c_*): favorito salvo, link em
      // card e o "Criar nova guia" das pendências não podem quebrar. Só as
      // rotas que eram React: /faturamento-tiss/dashboard, /faturas etc.
      // continuam sendo o TISS antigo em Vue (módulo tiss/manageGuides).
      // O alvo carrega o mesmo gate da flag — sem ela, cai em `/` como antes.
      {
        path: 'faturamento-tiss/spike/guia-consulta',
        redirect: to => ({
          path: '/faturamento/spike/guia-consulta',
          query: to.query
        })
      },
      {
        path: 'faturamento-tiss/guia/:tipo',
        redirect: to => ({
          path: `/faturamento/guia/${to.params.tipo}`,
          query: to.query
        })
      },
      {
        path: 'faturamento-tiss/workspace/lotes/:batchId/gerenciar-guias',
        redirect: to => ({
          path: `/faturamento/lotes/${to.params.batchId}/gerenciar-guias`,
          query: to.query
        })
      },
      {
        path: 'faturamento-tiss/workspace/:tab(guias|lotes|dashboard|faturas|configuracoes)?',
        redirect: to => ({
          path: to.params.tab
            ? `/faturamento/${to.params.tab}`
            : '/faturamento',
          query: to.query
        })
      },
      {
        path: 'painel-vendas/:tab?',
        component: SellingPanelHome,
        meta: { requiresAuth: true },
        beforeEnter: (to, from, next) =>
          customBeforeEnter('FpPaiVen1', to, from, next)
      },
      {
        // A Sessão de Caixa virou ABA do Financeiro (decisão de IA, 06/08):
        // a rota real é /financeiro/sessao_de_caixa, registrada em
        // modules/financial/router.js. Este redirect fica para não quebrar link
        // salvo de quem usou /caixas no ciclo anterior.
        path: 'caixas',
        redirect: '/financeiro/sessao_de_caixa'
      },
      {
        // Novo módulo de Centro Cirúrgico em React (strangler fig).
        // Renderizado dentro do layout Home → herda sidebar + header.
        //
        // pathMatch(.*)* captura QUALQUER sub-path para que o React Router
        // interno (BrowserRouter basename="/tracker-board") possa
        // resolver rotas como /surgery/:id, /rooms, /config/*.
        path: 'tracker-board/:pathMatch(.*)*',
        name: 'SurgeryCenterReact',
        component: SurgeryCenterReact,
        meta: { requiresAuth: true },
        // Exclusividade (ADR-006): só acessível com a flag v2 ligada na clínica.
        // Sem a flag, redireciona para o CC v1 (/centro-cirurgico-new).
        beforeEnter: (to, from, next) =>
          showSurgeryCenterV2()
            ? customBeforeEnter('CenCirurg1', to, from, next)
            : next('/centro-cirurgico-new/mapa-cirurgico')
      },
      // {
      // 	path: 'faturamento-tiss/:tab',
      // 	component: InvoicingTiss,
      // 	meta: {requiresAuth: true},
      // 	// beforeEnter: (to, from, next) => customBeforeEnter('Tiss', to, from, next)
      // },
      {
        path: 'laudos-medicos/:tab',
        component: MedicalReports,
        meta: { requiresAuth: true },
        beforeEnter: (to, from, next) =>
          customBeforeEnter('Laudo', to, from, next)
      },
      {
        path: 'laudo-medico/:patientId/:attendanceId/:medicalReportId',
        component: MedicalReport,
        meta: { requiresAuth: true },
        beforeEnter: (to, from, next) =>
          customBeforeEnter('Laudo', to, from, next)
      },
      { path: 'chat', component: ChatView, meta: { requiresAuth: true } },
      // {
      // 	path: 'pacientes',
      // 	component: PatientsList,
      // 	meta: {requiresAuth: true},
      // 	beforeEnter: (to, from, next) => customBeforeEnter('FpPac1', to, from, next)
      // },
      {
        path: 'pacientes/novo',
        component: PatientForm,
        meta: { requiresAuth: true },
        beforeEnter: (to, from, next) =>
          customBeforeEnter('FpPac2', to, from, next)
      },
      // {
      // 	path: 'pacientes/:id',
      // 	component: PatientForm,
      // 	meta: {requiresAuth: true},
      // 	beforeEnter: (to, from, next) => customBeforeEnter('FpPac3', to, from, next)
      // },
      // {
      // 	path: 'pacientes/:id/dashboard/:appointmentId?',
      // 	component: PatientDashboard,
      // 	meta: {requiresAuth: true},
      // 	beforeEnter: (to, from, next) => customBeforeEnter('FpPron1', to, from, next)
      // },
      {
        path: 'old/:id/consulta/:idConsulta',
        component: NewAttendance,
        meta: { requiresAuth: true },
        beforeEnter: (to, from, next) =>
          customBeforeEnter('FpPron2', to, from, next)
      },
      {
        path: 'pacientes/:id/anotacoes/:professionalId/:action',
        component: NewAnnotation,
        meta: { requiresAuth: true },
        beforeEnter: (to, from, next) =>
          customBeforeEnter('FpAno1', to, from, next)
      },
      {
        path: 'visao-geral',
        component: OverviewView,
        meta: { requiresAuth: true },
        // A Sidebar já escondia o item por papel (DOCTOR/DOCTOR_MANAGER), mas a
        // rota aceitava qualquer um por URL (C6 da avaliação da tela). Quem
        // não tem FpAgen1 continua entrando: é para cá que o redirect da raiz
        // manda esse usuário, e devolvê-lo para /agenda seria um laço.
        beforeEnter: (to, from, next) =>
          showDoctorOverview() || !userHasPermission('FpAgen1')
            ? next()
            : next('/agenda')
      },
      {
        path: 'analytics',
        component: AnalyticsView,
        meta: { requiresAuth: true }
      },
      {
        // Perfil de OUTRA pessoa: o id vem na query (`?profissional=<id>`).
        // Era params, que morria num F5 porque o path nunca teve o segmento.
        path: 'dados-pessoais',
        name: 'personalData',
        component: PersonalDataView,
        meta: { requiresAuth: true }
      },
      {
        // Os próprios dados — nunca recebe id.
        path: 'meus-dados-pessoais',
        name: 'myPersonalData',
        component: PersonalDataView,
        meta: { requiresAuth: true }
      },
      {
        path: 'configuracoes-da-clinica/:tab?',
        component: ClinicSettingsView,
        meta: { requiresAuth: true },
        beforeEnter: (to, from, next) =>
          customBeforeEnter('Configurações da clínica', to, from, next)
      },
      {
        // Rota antiga do detalhe por unidade (MU-20 PR 8c): a tela Vue
        // morreu no porte — redireciona para o detalhe por identidade.
        path: '/convenio/:id?/:tab?',
        component: () => import('@/views/Configs/ConvenioRedirect.vue'),
        meta: { requiresAuth: true }
      },
      {
        // Detalhe do convênio do GRUPO (MU-20 PR 8a) — identidade + vínculo
        // por unidade. As rotas antigas (/convenio, /convenios) continuam
        // servindo a visão por unidade até o PR 8c.
        path: '/convenios-do-grupo/:healthPlanId',
        component: () => import('@/views/Configs/ConvenioGrupoReact.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: 'agendamento-online',
        component: PublicScheduleView
        // meta: { requiresAuth: true },
        // beforeEnter: (to, from, next) => customBeforeEnter('Configurações agendamento online', to, from, next)
      },
      {
        // Listagem portada para React (lote 1). A aba é a URL, por isso o param
        // opcional; a regex evita que esta rota engula
        // /configuracoes-da-consulta/<uuid>, que continua no editor Vue abaixo.
        path: 'configuracoes-da-consulta/:tab(consultas|registros|prescricoes)?',
        component: () =>
          import('@/views/Configs/Appointment/AppointmentConfigReact.vue'),
        meta: { requiresAuth: true }
      },
      {
        // Editor de modelo em React: reusa as fichas da consulta 3.0 em
        // modo config (o que se digita vira default_text do modelo).
        path: 'configuracoes-da-consulta/:id',
        component: () =>
          import('@/views/Configs/Appointment/AppointmentEditorReact.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: 'configuracoes-do-exame/:id',
        component: () =>
          import('@/views/Configs/Appointment/AppointmentEditorReact.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: 'painel-de-chamadas/:tab?',
        component: CallPanel,
        meta: { requiresAuth: true }
      },
      {
        path: 'referrals/:tab?',
        component: Referrals,
        meta: { requiresAuth: true }
      },

      {
        // Alias antigo desta tela. Apontava para a listagem Vue (já portada),
        // que só continuava alcançável por aqui — agora redireciona.
        path: 'registros-padrao/:tab?',
        redirect: to => ({
          path: `/configuracoes-da-consulta/${to.params.tab || 'registros'}`
        })
      },
      // Registros padrões em React. As rotas com prefixo (oculos/biometria) vêm
      // antes de `:id` para não serem engolidas por ele.
      {
        path: 'registro-padrao/oculos/:id',
        component: StandardRecordsReact,
        props: { kind: 'oculos' },
        meta: { requiresAuth: true }
      },
      {
        path: 'registro-padrao/biometria/:id',
        component: StandardRecordsReact,
        props: { kind: 'biometria' },
        meta: { requiresAuth: true }
      },
      {
        path: 'registro-padrao/:id',
        component: StandardRecordsReact,
        props: { kind: 'structured' },
        meta: { requiresAuth: true }
      },
      {
        path: 'relatorios-gerenciais/:tab?',
        component: ManagementReports,
        meta: { requiresAuth: true },
        beforeEnter: (to, from, next) =>
          customBeforeEnter('Relatórios gerenciais', to, from, next)
      },
      {
        path: 'protocolos/:tab?',
        component: Protocols,
        meta: { requiresAuth: true },
        beforeEnter: (to, from, next) =>
          customBeforeEnter('FpProto1', to, from, next)
      },
      {
        path: 'pacientes/:patientId/consulta/:attendanceId',
        component: Attendance,
        meta: { requiresAuth: true },
        beforeEnter: (to, from, next) =>
          customBeforeEnter('FpPron2', to, from, next)
      },
      {
        path: 'pacientes/:patientId/pre-consulta/:attendanceId',
        component: Attendance,
        meta: { requiresAuth: true },
        beforeEnter: (to, from, next) =>
          customBeforeEnter('FpPreCon1', to, from, next)
      },
      {
        path: 'academy',
        component: Academy,
        meta: { requiresAuth: true }
      },
      {
        path: 'academy/curso/:courseId',
        component: CourseView,
        meta: { requiresAuth: true }
      },
      {
        path: 'glauco/dashboard',
        component: () => import('@/views/Glauco'),
        meta: { requiresAuth: true }
      },
      {
        path: 'glauco/dashboard/paciente/:patientId',
        component: () => import('@/views/Glauco/PatientDashboard'),
        meta: { requiresAuth: true }
      },

      {
        path: '/log-documentos',
        component: SignLogs,
        meta: { requiresAuth: true }
      },

      {
        path: 'solicitacao-estoque',
        component: () => import('@/views/Warehouse/StockRequests/History'),
        meta: { requiresAuth: true },
        beforeEnter: (to, from, next) =>
          userHasPermission('EstSol1') || userHasPermission('EstNovaSol1')
            ? next()
            : next({ name: 'noAccess' })
      },
      {
        path: '/estoque-setores',
        component: () => import('@/views/Warehouse/StockRequests/MyStock'),
        meta: { requiresAuth: true }
      },
      // @deprecated: Rota de configurações do CC legacy — oculta do menu desde CC v2.
      // NÃO remover: SurgicalNote.vue (/anotacao-cirurgica) contém links internos
      // que dependem desta rota (aba modelos-de-anotacao). Será substituída pelas
      // configurações do CC v2 quando a migração estiver concluída.
      {
        path: '/configuracoes-do-centro-cirurgico/:tab?',
        component: () => import('@/views/SurgeryCenter/Configuration'),
        meta: { requiresAuth: true }
      },
      {
        path: '/anotacao-cirurgica/:id?',
        component: () =>
          import('@/views/SurgeryCenter/Configuration/SurgicalNote'),
        meta: { requiresAuth: true }
      },
      {
        path: '/centro-cirurgico/mapa-cirurgico',
        component: () => import('@/views/SurgeryCenter/SurgicalMap'),
        meta: { requiresAuth: true }
      },
      {
        path: '/centro-cirurgico/lista-pacientes',
        component: () => import('@/views/SurgeryCenter/Surgeries'),
        meta: { requiresAuth: true }
      },
      {
        path: '/centro-cirurgico/dashboard/:surgeryId',
        component: () => import('@/views/SurgeryCenter/Dashboard'),
        meta: { requiresAuth: true }
      },
      {
        path: '/centro-cirurgico/dashboard/:surgeryId',
        component: () => import('@/views/SurgeryCenter/Dashboard'),
        meta: { requiresAuth: true }
      },
      {
        path: 'centro-cirurgico/dashboard/produtos/:surgeryId',
        component: () =>
          import('@/views/SurgeryCenter/MatMed/SurgeryItemsManagement'),
        meta: { requiresAuth: true }
      },
      {
        path: 'centro-cirurgico/matmed/:surgeryId',
        component: () =>
          import('@/views/SurgeryCenter/MatMed/MatMedManagement'),
        meta: { requiresAuth: true }
      },
      {
        path: 'pacientes/:patientId/cirurgia/:attendanceId/:surgeryId?',
        component: () => import('../layouts/Attendance'),
        meta: { requiresAuth: true }
      },
      {
        path: 'pacientes/:patientId/evolucao-anestesista/:attendanceId/:surgeryId',
        component: () => import('../layouts/Attendance'),
        meta: { requiresAuth: true }
      },
      {
        path: 'pacientes/:patientId/evolucao-equipe/:attendanceId/:surgeryId',
        component: () => import('../layouts/Attendance'),
        meta: { requiresAuth: true }
      },
      {
        path: 'pacientes/:patientId/anotacoes-equipe/:attendanceId/:surgeryId',
        component: () => import('../layouts/Attendance'),
        meta: { requiresAuth: true }
      }
    ]
  }
]

const router = new VueRouter({
  mode: 'history',
  base: import.meta.env.BASE_URL,
  routes
})

/***
 * Método para checar manutenção e autenticação
 */
router.beforeEach(async (to, from, next) => {
  // PRIMEIRA VERIFICAÇÃO: Verifica se o sistema está em modo de manutenção
  // Esta verificação deve acontecer ANTES de qualquer outra verificação
  // e bloqueia TODAS as rotas, incluindo /login, /, e qualquer outra rota
  if (MAINTENANCE_MODE === true) {
    // Se já estiver na página de manutenção, permite
    if (to.path === '/manutencao' || to.name === 'maintenance') {
      next()
      return
    }
    // Para QUALQUER outra rota (incluindo /login, /, etc), redireciona para manutenção
    // Usa replace: true para evitar que o usuário possa voltar
    next({ path: '/manutencao', replace: true })
    return
  }

  // Se não estiver em manutenção e estiver na página de manutenção, redireciona para home
  if (to.path === '/manutencao' && MAINTENANCE_MODE !== true) {
    next({ path: '/', replace: true })
    return
  }

  // Verifica autenticação normalmente (apenas se não estiver em manutenção)
  // Esta verificação só acontece se NÃO estiver em modo de manutenção
  if (
    to.matched.some(record => record.meta.requiresAuth) &&
    to.path !== '/preparando-documentos'
  ) {
    if (!Api.isLogged()) {
      next({ path: '/login' })
    } else {
      next()
    }
  } else {
    next()
  }
})

export const customBeforeEnter = (key, to, from, next) => {
  userHasPermission(key) ? next() : next({ name: 'noAccess' })
}

// Global router error handler
//
// Só falha de download de chunk (deploy novo + bundle velho em cache) justifica
// recarregar a página. Erro de código é reportado e exibido — recarregar em
// cima dele apagava o rastro da causa e virava ciclo de reload (86ak5gky5).
router.onError(error => {
  if (typeof window === 'undefined') {
    console.error('Router error:', error)
    return
  }

  handleRouterError(error, {
    toastInfo: message => Vue.prototype.$toast.info(message),
    toastError: message => Vue.prototype.$toast.error(message),
    reload: () => setTimeout(() => window.location.reload(), 1000),
    reportError: (err, context) => {
      console.error('Router error:', err)
      // Em dev/local o RUM não é inicializado (datadog.js) e addError é inócuo.
      datadogRum.addError(err, context)
    },
    storage: window.sessionStorage
  })
})

/***
 * Fim da classe para roteamento do VUE framework, para os componentes das views
 */

export default router
